import { APP_SETTINGS } from '../config/appSettings'
import { resolveLinkMetadataApiBaseUrl } from '../links/linkMetadata'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig } from '../supabase/config'
import { getNativePlatform } from '../utils/nativeRuntime'

/** What the person types to confirm. The server has its own literal, so a stray request cannot delete anything. */
export const DELETE_CONFIRMATION_PHRASE = 'delete my account'
const SERVER_CONFIRMATION = 'delete-my-account'
const RESUME_KEY = 'minimany:account-deletion:resume:v1'
const RESUME_WINDOW_MS = APP_SETTINGS.accountDeletion.recentSignInSeconds * 1000

export type AccountDeletionOutcome =
  | { status: 'deleted' }
  | { status: 'reauthentication_required' }
  | { status: 'failed'; message: string }

export function confirmationMatches(typed: string): boolean {
  return typed.trim().toLowerCase().replace(/\s+/g, ' ') === DELETE_CONFIRMATION_PHRASE
}

/** Asks the server to delete the signed-in account and everything in it. Immediate and final. */
export async function requestAccountDeletion(fetcher: typeof fetch = fetch): Promise<AccountDeletionOutcome> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { status: 'failed', message: 'You are offline. Connect to the internet to delete your account.' }
  }
  try {
    const client = await getSupabaseClient()
    const { data } = await client.auth.getSession()
    if (!data.session?.access_token) return { status: 'failed', message: 'You are signed out. Sign in again to delete your account.' }
    const base = resolveLinkMetadataApiBaseUrl({
      configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
      appEnvironment: getSupabaseConfig()?.environment ?? 'development',
      nativePlatform: getNativePlatform(window),
    })
    const response = await fetcher(`${base}/api/account/delete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${data.session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: SERVER_CONFIRMATION }),
    })
    const body = await response.json().catch(() => null) as { status?: unknown; error?: unknown } | null
    if (response.ok && body?.status === 'deleted') return { status: 'deleted' }
    if (body?.error === 'reauthentication_required') return { status: 'reauthentication_required' }
    if (body?.error === 'deletion_incomplete') {
      return { status: 'failed', message: 'Deleting did not finish. Your account still works and nothing else changed. Please try again.' }
    }
    return { status: 'failed', message: 'Your account could not be deleted right now. Nothing was changed. Please try again.' }
  } catch {
    return { status: 'failed', message: 'Your account could not be deleted right now. Check your connection and try again.' }
  }
}

/** When the person last signed in (seconds), from the token's authentication-methods claim; 0 when it has none. */
export function lastSignInSeconds(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { amr?: unknown }
    const stamps = Array.isArray(payload.amr)
      ? payload.amr.map((entry) => (entry as { timestamp?: unknown } | null)?.timestamp).filter((value): value is number => typeof value === 'number')
      : []
    return stamps.length ? Math.max(...stamps) : 0
  } catch { return 0 }
}

/** Whether the person signed in recently enough to delete now (the server decides again). Unknown counts as not recent. */
export async function signedInRecently(now = Date.now()): Promise<boolean> {
  try {
    const client = await getSupabaseClient()
    const { data } = await client.auth.getSession()
    const token = data.session?.access_token
    return Boolean(token) && now - lastSignInSeconds(token!) * 1000 <= RESUME_WINDOW_MS
  } catch { return false }
}

/** Remembers that the person went to sign in again for a deletion, so the dialog reopens when they return. */
export function markDeletionResume(now = Date.now()): void {
  try { localStorage.setItem(RESUME_KEY, String(now)) } catch { /* They can open the dialog again. */ }
}

/** True once, shortly after the person returns from signing in again for a deletion. */
export function takeDeletionResume(now = Date.now()): boolean {
  try {
    const stored = Number(localStorage.getItem(RESUME_KEY))
    localStorage.removeItem(RESUME_KEY)
    return Number.isFinite(stored) && stored > 0 && now - stored >= 0 && now - stored <= RESUME_WINDOW_MS
  } catch { return false }
}
